import { Assessment, Help, MoreVert } from '@mui/icons-material'
import {
  MenuItem,
  Button,
  Box,
  IconButton,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Tooltip,
  Menu,
  Link,
  Alert,
  AlertTitle,
} from '@mui/material'
import { FacebookPage } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import ConfirmDialog from '../../../lib/ConfirmDialog'
import { useFacebook } from '../../../lib/useFacebook'
import { useFeatureFlag } from '../../../lib/useFeatureFlag'
import useModalState from '../../../lib/useModalState'
import {
  userSelector,
  currentRetailerAccountSelector,
} from '../../../stores/app/selectors'
import useTracking from '../Tracking/useTracking'

import { RequestBusinessManagerPermissions } from './RequestBusinessManagerPermissions'

const useStyles = makeStyles()((theme) => {
  return {
    valid: {
      color: theme.palette.secondary.main,
      fontSize: '32px',
    },
    warning: {
      color: '#ff9800',
      fontSize: '32px',
    },
    error: {
      color: theme.palette.error.main,
      fontSize: '32px',
    },
    help: {
      color: theme.palette.grey[600],
      // TODO Just use Typography wherever this is used. This was part of the
      // mui-v5 upgrade.
      // https://github.com/garronej/tss-react/issues/131
      ...(theme.typography.subtitle1 as any),
      marginLeft: theme.spacing(1),
    },
    reportingStatus: {
      textAlign: 'center',
    },
    fixIt: {
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.secondary.contrastText,
    },
    refresh: {
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
    },
    academy: {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText,
      textAlign: 'left',
    },
    titleGranted: {
      color: theme.palette.success.dark,
    },
    titleMissing: {
      color: theme.palette.error.main,
    },
  }
})

interface HelpTipProps {
  title: string
}

function HelpTip({ title }: HelpTipProps) {
  const { classes } = useStyles()
  return (
    <Tooltip title={title}>
      <Help className={classes.help} />
    </Tooltip>
  )
}

interface FacebookPermissionsTableProps {
  hasUserControl: boolean
  facebookPage: FacebookPage
  fixFacebookAccountPermissions: () => void
  fixInstagramBusinessAccount: () => void
  deleteFacebookPage: (id: string) => void
}

export function FacebookPermissionsTable({
  hasUserControl,
  facebookPage,
  fixFacebookAccountPermissions,
  fixInstagramBusinessAccount,
  deleteFacebookPage,
}: FacebookPermissionsTableProps) {
  const isBusinessManager = facebookPage.business_manager_permissions === true

  const { track } = useTracking()
  const { classes } = useStyles()
  const isFacebookRequestBusinessManagerAccessEnabled = useFeatureFlag(
    'facebook-request-business-manager-access',
  )
  const { isOpen, closeModal, openModal } = useModalState()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const currentUser = useSelector(userSelector) //TODO: Eventually replace this with nice hook
  const retailerAccount = useSelector(currentRetailerAccountSelector) //TODO: Eventually replace this with nice hook

  const { getPermissionsStatus, manualBusinessRequest } = useFacebook(
    currentUser,
    retailerAccount,
  )

  const handleDeleteFacebookPage = useCallback(() => {
    setAnchorEl(null)
    deleteFacebookPage(facebookPage.id)
  }, [facebookPage, deleteFacebookPage])

  const permissionsStatus = getPermissionsStatus(facebookPage)
  const permissionWarnings: string[] = []
  if (permissionsStatus) {
    if (permissionsStatus.hasPermissionAds === false) {
      permissionWarnings.push(
        I18n.t('facebookPermissionsManager.warnings.hasPermissionAds'),
      )
    }
    if (permissionsStatus.hasPermissionAdsInsights === false) {
      permissionWarnings.push(
        I18n.t('facebookPermissionsManager.warnings.hasPermissionAdsInsights'),
      )
    }
    if (permissionsStatus.hasPermissionOrganic === false) {
      permissionWarnings.push(
        I18n.t('facebookPermissionsManager.warnings.hasPermissionOrganic'),
      )
    }
    if (permissionsStatus.hasPermissionOrganicInsights === false) {
      permissionWarnings.push(
        I18n.t(
          'facebookPermissionsManager.warnings.hasPermissionOrganicInsights',
        ),
      )
    }
    if (permissionsStatus.hasPermissionInstagramDesktop === false) {
      permissionWarnings.push(
        I18n.t(
          'facebookPermissionsManager.warnings.hasPermissionInstagramDesktop',
        ),
      )
    }
    if (permissionsStatus.hasPermissionInstagramDesktopInsights === false) {
      permissionWarnings.push(
        I18n.t(
          'facebookPermissionsManager.warnings.hasPermissionInstagramDesktopInsights',
        ),
      )
    }
  }

  const hasInstagramBusinessAccount =
    !!facebookPage.instagram_business_account_id

  const openAcademyForInstagram = () => {
    window.open(
      'https://support.promoboxx.com/hc/en-us/articles/15363080988443-Connecting-your-Instagram-to-your-Facebook-for-Direct-Posting',
      '_blank',
    )
    track({
      event: 'FacebookPermissionsManager',
      data: {
        name: 'learn more about instagram business account',
        userId: currentUser.id,
        retailerAccountId: retailerAccount.id,
        facebookPageId: facebookPage.id,
      },
    })
  }

  const { grantedPermissions, missingPermissions } = useMemo(() => {
    const grantedPermissions: PermissionSummary[] = []
    const missingPermissions: PermissionSummary[] = []

    if (permissionsStatus != null) {
      const allPermissions: PermissionSummary[] = [
        {
          hasInsights: permissionsStatus.hasPermissionOrganicInsights,
          isValid: permissionsStatus.hasPermissionOrganic,
          title: I18n.t('facebookPermissionsManager.abilityOrganic'),
          description: I18n.t(
            'facebookPermissionsManager.abilityDescriptionOrganic',
          ),
          tooltip: I18n.t('facebookPermissionsManager.abilityTooltipOrganic'),
          tooltipLink:
            'https://support.promoboxx.com/hc/en-us/sections/15307591070747-How-to-Share',
        },
        {
          hasInsights:
            permissionsStatus.hasPermissionAdsInsights && isBusinessManager,
          isValid: permissionsStatus.hasPermissionAds && isBusinessManager,
          title: I18n.t('facebookPermissionsManager.abilityAds'),
          description: I18n.t(
            'facebookPermissionsManager.abilityDescriptionAds',
          ),
          tooltip: I18n.t('facebookPermissionsManager.abilityTooltipAds'),
          tooltipLink:
            'https://support.promoboxx.com/hc/en-us/articles/15363098472219-How-To-Set-Up-Your-Account-For-Premium-Ads',
        },
        {
          hasInsights:
            permissionsStatus.hasPermissionInstagramDesktopInsights === true &&
            hasInstagramBusinessAccount,
          isValid:
            permissionsStatus.hasPermissionInstagramDesktop === true &&
            hasInstagramBusinessAccount,
          title: I18n.t('facebookPermissionsManager.abilityInstagramDesktop'),
          description: I18n.t(
            'facebookPermissionsManager.abilityDescriptionInstagramDesktop',
          ),
          tooltip: I18n.t(
            'facebookPermissionsManager.abilityTooltipInstagramDesktop',
          ),
          tooltipLink:
            'https://support.promoboxx.com/hc/en-us/articles/15363098619419-Sharing-Directly-to-Instagram-via-Promoboxx',
        },
      ]

      for (const permission of allPermissions) {
        // TODO Is this all we want to check on before considering a permission
        // "bad"?
        if (permission.isValid) {
          grantedPermissions.push(permission)
        } else {
          missingPermissions.push(permission)
        }
      }
    }

    return {
      grantedPermissions,
      missingPermissions,
    }
  }, [hasInstagramBusinessAccount, isBusinessManager, permissionsStatus])

  if (permissionsStatus) {
    return (
      <>
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}
        >
          <Typography variant={'h6'}>{facebookPage.name}</Typography>
          <IconButton
            aria-haspopup={'true'}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            size="large"
          >
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            keepMounted={true}
            onClose={() => setAnchorEl(null)}
            open={Boolean(anchorEl)}
          >
            <MenuItem onClick={openModal}>
              {I18n.t('facebookPermissionsManager.removeThisPage')}
            </MenuItem>
          </Menu>
        </Box>
        {hasUserControl &&
          !isBusinessManager &&
          isFacebookRequestBusinessManagerAccessEnabled && (
            <Box mb={2}>
              <Alert
                action={
                  <RequestBusinessManagerPermissions
                    facebookPage={facebookPage}
                    manualBusinessRequest={manualBusinessRequest}
                  />
                }
                severity={'warning'}
              >
                <AlertTitle>
                  {I18n.t('facebookPermissionsManager.warning')}
                </AlertTitle>
                <ul>
                  <li>
                    <Typography variant={'caption'}>
                      {facebookPage.manual_business_manager_requested_at
                        ? I18n.t(
                            'facebookPermissionsManager.warnings.notBusinessManagerRequested',
                          )
                        : I18n.t(
                            'facebookPermissionsManager.warnings.notBusinessManager',
                          )}
                    </Typography>
                  </li>
                </ul>
              </Alert>
            </Box>
          )}
        {hasUserControl &&
          permissionWarnings &&
          permissionWarnings.length > 0 && (
            <Box mb={2}>
              <Alert
                action={
                  <Button
                    className={classes.fixIt}
                    color={'secondary'}
                    onClick={fixFacebookAccountPermissions}
                    size={'small'}
                    variant={'contained'}
                  >
                    {I18n.t('facebookPermissionsManager.fixIt')}
                  </Button>
                }
                severity={'warning'}
              >
                <AlertTitle>
                  {I18n.t('facebookPermissionsManager.warning')}
                </AlertTitle>
                <ul>
                  {permissionWarnings.map((warning) => (
                    <li key={`warning-${warning.split(' ').join('-')}`}>
                      <Typography variant={'caption'}>{warning}</Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            </Box>
          )}
        {!hasInstagramBusinessAccount && hasUserControl && (
          <Box mb={2}>
            <Alert
              action={
                <Button
                  className={classes.refresh}
                  // color={'info'}
                  onClick={fixInstagramBusinessAccount}
                  size={'small'}
                  variant={'contained'}
                >
                  {I18n.t('facebookPermissionsManager.refresh')}
                </Button>
              }
              severity={'info'}
            >
              <AlertTitle>
                {I18n.t('facebookPermissionsManager.info')}
              </AlertTitle>
              <ul>
                <li>
                  <Typography variant={'caption'}>
                    {I18n.t(
                      'facebookPermissionsManager.warnings.hasInstagramBusinessAccount',
                    )}
                  </Typography>
                </li>
              </ul>
              <Button
                className={classes.academy}
                onClick={openAcademyForInstagram}
                size={'small'}
                variant={'text'}
              >
                <Help style={{ marginRight: '10px' }} />
                <Typography variant={'caption'}>
                  {I18n.t(
                    'facebookPermissionsManager.instagramBusinessAccountInstructions',
                  )}
                </Typography>
              </Button>
            </Alert>
          </Box>
        )}
        {!hasUserControl && (
          <Box mb={2}>
            <Tooltip
              title={I18n.t(
                'facebookPermissionsManager.mightBeSocialMediaManager',
              )}
            >
              <Alert severity={'error'}>
                <Typography>
                  {I18n.t(
                    'facebookPermissionsManager.invalidPageTokenNoAccess',
                  )}
                </Typography>
              </Alert>
            </Tooltip>
          </Box>
        )}

        {missingPermissions.length > 0 ? (
          <Box marginY={2}>
            <Typography variant={'h6'} gutterBottom>
              {I18n.t('facebookPermissionsManager.missingPermissions')}
            </Typography>
            <PermissionSummaryTable permissions={missingPermissions} />
          </Box>
        ) : undefined}

        {grantedPermissions.length > 0 ? (
          <Box marginY={2}>
            <Typography variant={'h6'} gutterBottom>
              {I18n.t('facebookPermissionsManager.grantedPermissions')}
            </Typography>
            <PermissionSummaryTable permissions={grantedPermissions} />
          </Box>
        ) : undefined}

        <ConfirmDialog
          content={I18n.t('facebookPermissionsManager.removeThisPageConfirm', {
            pageName: facebookPage.name,
          })}
          onClose={() => {
            setAnchorEl(null)
            closeModal()
          }}
          onConfirmClick={handleDeleteFacebookPage}
          open={isOpen}
          title={I18n.t('facebookPermissionsManager.removeThisPageTitle')}
        />
      </>
    )
  } else {
    return null
  }
}

interface PermissionSummary {
  hasInsights: boolean
  isValid: boolean
  title: string
  description: string
  tooltip?: string
  tooltipLink?: string
}

const PermissionSummaryTable: React.FC<
  React.PropsWithChildren<{ permissions: PermissionSummary[] }>
> = (props) => {
  const { classes, cx } = useStyles()

  if (props.permissions.length === 0) {
    return null
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableBody>
          {props.permissions.map((summary) => {
            let tooltip: React.ReactNode = null

            if (summary.tooltip) {
              tooltip = <HelpTip title={summary.tooltip} />

              if (summary.tooltipLink) {
                tooltip = (
                  <a
                    href={summary.tooltipLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {tooltip}
                  </a>
                )
              }
            }

            return (
              <TableRow>
                <TableCell>
                  <Typography>
                    <span
                      className={cx({
                        [classes.titleGranted]: summary.isValid,
                        [classes.titleMissing]: !summary.isValid,
                      })}
                    >
                      {summary.title}
                    </span>
                    {tooltip}
                  </Typography>
                  <Typography variant={'caption'}>
                    {summary.description}
                    <br />
                    {!summary.isValid ? (
                      <Link
                        href="https://support.promoboxx.com/hc/en-us/articles/15363051188123-How-do-I-enable-Facebook-permissions-"
                        target="_blank"
                      >
                        {I18n.t(
                          'facebookPermissionsManager.howToFixMissingPermissions',
                        )}
                      </Link>
                    ) : undefined}
                  </Typography>
                </TableCell>
                <TableCell>
                  {summary.hasInsights ? (
                    <Box
                      alignItems={'center'}
                      display={'flex'}
                      flexDirection={'column'}
                    >
                      <Assessment color={'primary'} fontSize={'large'} />
                      <Typography
                        className={classes.reportingStatus}
                        variant={'caption'}
                      >
                        {I18n.t('facebookPermissionsManager.reportingEnabled')}
                      </Typography>
                    </Box>
                  ) : undefined}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
